import React, { useState, useEffect, Fragment, useMemo } from 'react'
import { Link, useRouteMatch } from 'react-router-dom'
import { useDispatch } from 'react-redux'
import injectStyles from 'react-jss'
import EditIcon from '@material-ui/icons/Edit'
import Checkbox from '@material-ui/core/Checkbox'
import Box from '@material-ui/core/Box'
import moment from 'moment'

import CityPage from 'Containers/City/Page'
import { MasterScheduleWrapper } from 'Components/City/Page/MasterSchedule'
import StyledIconButton from 'Components/Form/StyledIconButton'
import { StatusLabel } from 'Components/StatusLabel'
import LoadingIndicator from 'Components/LoadingIndicator/LoadingIndicator'
import EnhancedTable from 'Components/EnhancedTable'
import MissionContextMenu from './MissionContextMenu'
import { ModalConfirmation } from 'Components/Modal/ModalConfirmation'
import { MissionStarRatingWithHint } from 'Components/MissionStarRatingWithHint'
import { Button } from 'Components/Buttons/Button'
import DeleteIcon from 'Assets/Images/icons/delete'
import useGraphQLFetch from 'Utils/Hooks/useGraphQLFetch'
import {
  defaultMissionFilters,
  getOptions,
  getWeeksFilterOptions,
} from 'Utils/Table'
import { formatStringToFirstUpperCase } from 'Utils/Formatters'
import { getCurrentTimeOfDay } from 'Utils/Date'
import { urlGet, urlRemoveExist } from 'Utils/URL'
import { MISSION_STATUS_ENUM } from 'shared/constants'
import { getLongWeekDay, getWeekDayIndex } from 'shared/libraries/utils/date'
import {
  hideReduxModal,
  resetReduxModal,
  showReduxModal,
} from 'Reducers/UI/actions'
import {
  formatRows,
  setUrl,
  switchOrderBy,
  getUpdatedQueryAndLabels,
  getUpdatedMappedFilters,
  onClickRow,
  getTitle,
  filterSpies,
  calculateTotals,
  openBaPage,
  exportCSV,
} from './utils'
import {
  getMissionsByCountry,
  getMissionsByCity,
  updateMission,
  bookSpyMissions,
  deleteSpyShift,
  addMissionOnExistingShift,
  getCitySettingsData,
} from './gql'
import AssignSpyToolbar from './AssignSpyToolbar'
import SearchAndFilters from './Filters'
import {
  defaultModalState,
  headers,
  MODAL_TYPES,
  presets as initialPresets,
  countryLevelPresets,
} from './options'
import { styles } from './styles'
import { authorization } from '../../../../Module/Auth/Auth'
import { MISSION_ENUM_TYPES, MONTHS_LIST } from '../../../../shared/constants'
import { MISSION_NO_SHOWN_STATUS } from '../../../../constants'
import { getLocationProfileLink } from '../../../../Utils/Link'

const MasterSchedule = props => {
  const {
    classes = {},
    clients,
    locations,
    campaigns,
    cityId,
    afternoonCheckInTime,
    lunchCheckInTime,
    timezone,
    spySettings,
    spies,
    countryId,
    citiesList,
    cityName,
    countryName,
    simpleCountryId,
  } = props

  const [rowsData, setRowsData] = useState([])
  const [totalRow, setTotalRow] = useState(null)
  const [order, setOrder] = useState('ASC')
  const [orderBy, setOrderBy] = useState(null)
  const [resultPerPage, setResultPerPage] = useState(50)
  const [skip, setSkip] = useState(0)
  const [noShow, setNoShow] = useState(false)
  const [totalCount, setTotalCount] = useState(0)
  const [page, setPage] = useState(0)
  const [searchText, setSearchText] = useState('')
  const [mappedFilters, setMappedFilters] = useState([])
  const [suggestions, setSuggestions] = useState([])
  const [activeTags, setActiveTags] = useState([])
  const [presetValue, setPresetValue] = useState('No Preset')
  const [selectedMissions, setSelectedMissions] = useState([])
  const [changedCheckBox, setChangedCheckBox] = useState({})
  const [customRefetch, setCustomRefetch] = useState(false)
  const [currentData, setCurrentData] = useState([])
  // Right-click menu on a mission: where it opened, and which mission.
  const [contextMenu, setContextMenu] = useState(null)
  const [modalInformation, setModalInformation] = useState(defaultModalState)
  const [filteredSpies, setFilteredSpies] = useState(spies)
  const [title, setTitle] = useState(
    getTitle({
      numSelected: selectedMissions.length,
    }),
  )
  const [selectedFilters, setSelectedFilters] = useState({
    query: {},
    labels: {},
  })
  const [isExportLoading, setIsExportLoading] = useState(false)
  const presets = useMemo(() => {
    const items = countryId ? countryLevelPresets : initialPresets
    return items
  }, [countryId])

  const isCountryLevel = Boolean(countryId)
  const isCityManagerAssistant = authorization.isCityManagerAssistant()
  const isSpy = authorization.isSpy()
  const shouldHideSensitiveData = isCityManagerAssistant

  useEffect(() => {
    if (selectedMissions[0]) {
      const result = filterSpies({
        spies,
        missionId: selectedMissions[0],
        rowsData,
      })
      setFilteredSpies(result)
    }
  }, [selectedMissions[0]])

  const match = useRouteMatch()
  const dispatch = useDispatch()

  const currentTimeOfDay = getCurrentTimeOfDay({
    afternoonCheckInTime,
    lunchCheckInTime,
    timezone,
  })

  const { data, isLoaded, refetch } = useGraphQLFetch({
    fetchFn: countryId ? getMissionsByCountry : getMissionsByCity,
    defaultFetchArgs: {
      id: countryId,
      cityId,
      first: resultPerPage,
      skip,
      orderBy,
      filter: searchText,
      noShow,
      ...selectedFilters.query,
    },
    mapResponse: data => formatRows(data, !!countryId),
    skipFetchOnInit: true,
  })

  const cityResponse = cityId
    ? useGraphQLFetch({
        fetchFn: getCitySettingsData,
        defaultFetchArgs: cityId,
        fetchId: 'getCitySettingsData',
        mapResponse: data => data.city,
      })
    : {}

  const teamLeaderEnabled =
    cityResponse && cityResponse.data && cityResponse.data.teamLeaderEnabled

  useEffect(() => {
    const filters = [
      ...(countryId
        ? [
            {
              label: 'City',
              options: getOptions(citiesList),
            },
          ]
        : []),
      ...[
        {
          label: 'Client',
          options: getOptions(clients),
        },
        {
          label: 'Location',
          options: getOptions(locations),
        },
        {
          label: 'Campaign',
          options: getOptions(campaigns),
        },
        {
          label: 'Week',
          options: getWeeksFilterOptions(),
        },
        {
          label: 'Month',
          options: MONTHS_LIST.map((month, i) => ({
            value: (i + 1).toString(),
            label: month,
          })),
          multiple: true,
        },
        {
          label: 'Year',
          options: [...Array(new Date().getFullYear() + 1 - 2013).keys()].map(
            i => {
              const year = (new Date().getFullYear() + 1 - i).toString()
              return { value: year, label: year }
            },
          ),
        },
        ...defaultMissionFilters,
        {
          label: 'Reported',
          options: [
            { label: 'Only Reported', value: true },
            { label: 'Only Not Reported', value: false },
          ],
        },
      ],
    ]
    if (locations.length || clients.length || campaigns.length) {
      const search = urlGet('search')
      if (search) {
        setSearchText(search)
        setTagsAndSuggestions({ tag: search, option: 'add' })
      }
      const newFilters = filters.map(({ label, options }, index) => {
        let selectedValue = 'All'
        let labelValues = urlGet(label)
        const labels = options.map(({ label }) => label)
        setSuggestions(prev => [...prev, ...labels])
        if (labelValues) {
          if (!Array.isArray(labelValues)) labelValues = [labelValues]
          labelValues.forEach(labelValue => {
            const findOption = options.find(item => item.label === labelValue)
            const queryValue = findOption ? findOption.value : null
            setTagsAndSuggestions({ tag: labelValue, option: 'add' })
            setSelectedFilters(prev =>
              getUpdatedQueryAndLabels(prev, [
                { label, labelValue, queryValue },
              ]),
            )
            selectedValue = queryValue
          })
        }
        return {
          data: {
            label,
            options,
            selectedValue,
          },
          config: {
            type: 'select',
            filterIndex: index,
          },
        }
      })
      setMappedFilters(newFilters)
    }
  }, [])

  useEffect(() => {
    if (isLoaded && data) {
      setCurrentData(data.nodes)
      setTotalCount(data.totalCount)
    }
  }, [isLoaded])

  useEffect(() => {
    if (isLoaded && data) {
      setTitle(
        getTitle({
          numSelected: selectedMissions.length,
        }),
      )
    }

    if (currentData.length) {
      setRowsData(createRows(currentData))
    }
  }, [selectedMissions.length, isLoaded])

  useEffect(() => {
    setRowsData(createRows(currentData))
    calculateTotals({
      data: currentData,
      setTotalRow: setTotalRow,
      isCountryLevel,
      isCityManagerAssistant,
      isSpy,
    })
  }, [currentData.length])

  useEffect(() => {
    if (currentData.length && changedCheckBox.id) {
      setCurrentData(prev => {
        return prev.reduce((acc, item) => {
          if (item.id === changedCheckBox.id) {
            acc.push({
              ...item,
              canBeSpied: changedCheckBox.canBeSpied,
              spy: changedCheckBox.canBeSpied ? item.spy : false,
            })
          } else {
            acc.push({ ...item })
          }
          return acc
        }, [])
      })

      setCustomRefetch(prev => !prev)
      setRowsData(createRows(currentData))
    }
  }, [changedCheckBox.id, changedCheckBox.canBeSpied])

  useEffect(() => {
    if (currentData.length && changedCheckBox.id) {
      setRowsData(createRows(currentData))
    }
  }, [customRefetch])

  useEffect(() => {
    refetchAndDropSelectedMissions({
      countryId,
      cityId,
      first: resultPerPage,
      skip,
      orderBy,
      filter: searchText,
      excludeArchivedCampaigns: true,
      noShow,
      ...selectedFilters.query,
    })
  }, [
    orderBy,
    resultPerPage,
    skip,
    ...Object.values(selectedFilters.query),
    searchText,
    presetValue,
    noShow,
  ])

  const refetchAndDropSelectedMissions = refetchValues => {
    setSelectedMissions([])
    setCurrentData([])
    refetch(refetchValues)
  }

  const onContextMenuRow = (event, item) => {
    // Spies and clients get the browser's own menu.
    if (isSpy || !item || !item.params) return
    const row = currentData.find(
      mission => String(mission.id) === String(item.params.id),
    )
    if (!row) return
    event.preventDefault()
    setContextMenu({
      anchor: { mouseX: event.clientX, mouseY: event.clientY },
      row,
    })
  }

  const refetchAfterMenuAction = () =>
    refetchAndDropSelectedMissions({
      countryId,
      cityId,
      first: resultPerPage,
      skip,
      orderBy,
      filter: searchText,
      excludeArchivedCampaigns: true,
      noShow,
      ...selectedFilters.query,
    })

  const onChangePage = page => {
    setSkip(resultPerPage * page)
    setPage(page)
  }

  const onChangeRowsPerPage = value => {
    setResultPerPage(value)
    setPage(0)
  }

  const onChange = ({ value, label, data }) => {
    const findOption = data.options.find(item => item.value === value)
    const labelValue = findOption ? findOption.label : null
    if (labelValue) {
      setTagsAndSuggestions({ tag: labelValue, option: 'add' })
    } else {
      const values = selectedFilters.labels[label]
      if (values) {
        values.forEach(value => {
          setTagsAndSuggestions({ tag: value, option: 'remove' })
        })
      }
    }
    setMappedFilters(prev =>
      getUpdatedMappedFilters({
        prev,
        updatedValues: [
          {
            label,
            queryValue: value,
          },
        ],
        isOnlyOne: true,
      }),
    )
    setUrl(label, labelValue)
    setSelectedFilters(prev =>
      getUpdatedQueryAndLabels(prev, [
        { label, labelValue, queryValue: value },
      ]),
    )
    setSkip(0)
  }

  const onAddTag = tag => {
    setPresetValue('No Preset')
    const data = mappedFilters.reduce((acc, item) => {
      const option = item.data.options.find(option => option.label === tag)
      if (option) {
        acc = { ...item.data, option }
      }
      return acc
    }, null)
    if (data) {
      setSelectedFilters(prev =>
        getUpdatedQueryAndLabels(prev, [
          {
            label: data.label,
            labelValue: data.option.label,
            queryValue: data.option.value,
          },
        ]),
      )

      setMappedFilters(prev =>
        getUpdatedMappedFilters({
          prev,
          updatedValues: [
            {
              label: data.label,
              queryValue: data.option.value,
            },
          ],
          isOnlyOne: true,
        }),
      )

      setUrl(data.label, tag)
    }
    setTagsAndSuggestions({ tag, option: 'add' })
  }

  const onRemoveTag = tag => {
    setPresetValue('No Preset')
    const labels = Object.entries(selectedFilters.labels).find(
      ([label, value]) => value && value.includes(tag),
    )
    const label = labels ? labels[0] : null
    if (label) {
      const { data } = mappedFilters.find(({ data }) => data.label == label)
      let newQueryValue = 'All'
      const newOptionKey = selectedFilters.labels[label].find(
        value => value != tag,
      )
      if (newOptionKey) {
        const optionData = mappedFilters.reduce((acc, item) => {
          const option = item.data.options.find(
            option => option.label === newOptionKey,
          )
          if (option) {
            acc = { ...item.data, option }
          }
          return acc
        }, null)
        if (optionData) newQueryValue = optionData.option.value
      }
      if (data) {
        const { options } = data
        const findOption = options.find(item => item.label === tag)
        const queryValue = findOption ? findOption.value : null
        urlRemoveExist(label, tag)
        setSelectedFilters(prev =>
          getUpdatedQueryAndLabels(
            prev,
            [
              {
                label,
                labelValue: tag,
                queryValue: queryValue,
              },
            ],
            false,
          ),
        )
        setMappedFilters(prev =>
          getUpdatedMappedFilters({
            prev,
            updatedValues: [
              {
                label,
                queryValue: newQueryValue,
              },
            ],
            isOnlyOne: true,
          }),
        )
      }
    } else {
      setSearchText(null)
      setUrl('search')
    }
    setTagsAndSuggestions({ tag, option: 'remove' })
  }

  const createRows = data =>
    data.map(row => {
      const warning = `Are you sure you want to delete ${
        row.spy
      } from this mission?
        
        "ATTENTION: Removing spy from this mission will also affect all other missions in this shift"`
      const result = {
        params: {
          highLight: row.isReported,
          id: row.id,
          disableClick: ['canBeSpied', 'spy', 'brandAmbassador', 'location'],
        },
        row: {
          ...(countryId ? { cityName: row.cityName } : {}),
          ...{
            clientName: row.clientName,
            campaignName: row.campaignName,
            week: row.week,
            date: row.date,
            time: row.time,
            timeOfDay: row.timeOfDay,
            dayOfWeek: row.dayOfWeek,
            canBeSpied:
              [
                MISSION_STATUS_ENUM.BOOKED,
                MISSION_STATUS_ENUM.CHECKED_IN,
              ].includes(row.status) &&
              (!row.teamLeaderGroupId || !teamLeaderEnabled) ? (
                <Checkbox
                  disabled={
                    (countryId &&
                    citiesList.find(
                      city =>
                        city.name === row.cityName &&
                        city.hasOwnProperty('spySettings'),
                    )
                      ? !citiesList.find(city => city.name === row.cityName)
                          .spySettings
                      : !!spySettings
                      ? !spySettings
                      : row.isSpied) || isSpy
                  }
                  checked={row.canBeSpied}
                  onChange={async (event, canBeSpied) => {
                    if (!row.shiftId) {
                      const response = await updateMission({
                        id: row.id,
                        canBeSpied,
                      })
                      const { data } = await response.json()
                      const mission = data.updateMission.mission

                      if (mission.id) {
                        setChangedCheckBox(mission)

                        // update selected missions state if we set mission
                        // to canBeSpied = false
                        !canBeSpied &&
                          setSelectedMissions(prev => [
                            ...prev.filter(item => item !== mission.id),
                          ])
                      }
                    } else {
                      setModalInformation({
                        text: warning,
                        shiftId: row.shiftId,
                        isOpen: true,
                        type: MODAL_TYPES.UPDATE_MISSION,
                        missionId: row.id,
                        canBeSpied,
                      })
                    }
                  }}
                />
              ) : (
                ''
              ),
            spy: row.spy ? (
              <div className={classes.spyColumn}>
                <div>{row.spy}</div>
                <div className={classes.deleteIconWrapper}>
                  {[
                    MISSION_STATUS_ENUM.BOOKED,
                    MISSION_STATUS_ENUM.CHECKED_IN,
                  ].includes(row.status) &&
                  !row.isSpied &&
                  (!row.teamLeaderGroupId || !teamLeaderEnabled) &&
                  !isSpy ? (
                    <DeleteIcon
                      onClick={() => {
                        setModalInformation({
                          text: warning,
                          shiftId: row.shiftId,
                          isOpen: true,
                          type: MODAL_TYPES.DELETE_SPY,
                        })
                      }}
                      width={18}
                      height={18}
                      fill="#8A96A0"
                    />
                  ) : null}
                </div>
              </div>
            ) : row.canBeSpied &&
              (row.status === MISSION_STATUS_ENUM.BOOKED ||
                row.status === MISSION_STATUS_ENUM.CHECKED_IN) ? (
              <Checkbox
                onChange={async (e, value) => {
                  if (value) {
                    setSelectedMissions(prev => [...prev, row.id])
                  } else {
                    setSelectedMissions(prev => [
                      ...prev.filter(item => item !== row.id),
                    ])
                  }
                }}
              />
            ) : (
              ' - '
            ),
            location:
              row.locationId &&
              row.type === MISSION_ENUM_TYPES.HANDTOHAND &&
              !isCityManagerAssistant &&
              !isSpy ? (
                <a
                  href={getLocationProfileLink(row.cityId, row.locationId)}
                  target={'_blank'}
                  style={styles.link}
                  rel="noreferrer"
                >
                  {row.location}
                </a>
              ) : (
                row.location
              ),
            brandAmbassador: isSpy ? (
              row.brandAmbassador
            ) : (
              <Link
                onClick={() => openBaPage(row.cityId, row.baId)}
                style={styles.link}
              >
                {row.brandAmbassador}
              </Link>
            ),
            flyerCode: row.flyerCode,
            tag: row.tag,
            flyersDistributed: row.flyersDistributed
              ? row.flyersDistributed
              : 0,
            scans: row.scans,
            metric1: row.metric1,
            metric2: row.metric2,
            status: (
              <div>
                <StatusLabel isSpied={row.isSpied} status={row.status} />
                {row.isNotShown && (
                  <StatusLabel status={MISSION_NO_SHOWN_STATUS} />
                )}
              </div>
            ),
            rating:
              row.status in
              {
                'UNDER_REVIEW': true,
                'COMPLETED': true,
                'PAID': true,
              } ? (
                <div
                  onClick={e => {
                    e.preventDefault()
                    e.stopPropagation()
                  }}
                >
                  <MissionStarRatingWithHint
                    uniqueId={'rate-' + row.id}
                    id={'rate-' + row.id}
                    ratingInfo={row.ratingInfo}
                  />
                </div>
              ) : (
                ' - '
              ),
          },
        },
      }
      if (isCityManagerAssistant) {
        delete result.row.metric1
        delete result.row.metric2
      }
      if (isSpy) {
        delete result.row.flyerCode
        delete result.row.metric1
        delete result.row.metric2
        delete result.row.rating
      }
      return result
    })

  const handleRequestSort = (event, property) => {
    const sort = !orderBy ? 'ASC' : order === 'ASC' ? 'DESC' : 'ASC'
    const column = switchOrderBy(property)
    setOrderBy(column)
    const value = `${column.toUpperCase()}_${sort}`
    setOrder(sort)
    setOrderBy(value)
  }

  const onPressInput = searchValue => {
    setUrl('search', searchValue)
    setSearchText(searchValue)
    setPresetValue('No Preset')

    refetchAndDropSelectedMissions({
      countryId,
      cityId,
      first: resultPerPage,
      skip,
      orderBy,
      filter: searchValue,
      ...selectedFilters.query,
    })
  }

  const setTagsAndSuggestions = ({ tag, option }) => {
    if (option === 'add') {
      setActiveTags(prev => [...prev.filter(item => item !== tag), tag])
      setSuggestions(prev => prev.filter(item => item !== tag))
    }
    if (option === 'remove') {
      setActiveTags(prev => prev.filter(item => item !== tag))
      setSuggestions(prev => [...prev.filter(item => item !== tag), tag])
    }
  }

  const onChangePreset = ({ id, label }) => {
    setPresetValue(label)
    let updateQuery = []
    const keys = Object.keys(selectedFilters.labels)
    if (keys.length) {
      keys.forEach(key => {
        setUrl(key)
        const { data } = mappedFilters.find(({ data }) => data.label == key)
        const tags = selectedFilters.labels[key]
        if (tags) {
          tags.forEach(tag => {
            setTagsAndSuggestions({
              tag,
              option: 'remove',
            })
            if (data) {
              const options = data.options
              const findOption = options.find(item => item.label === tag)
              const queryValue = findOption ? findOption.value : null
              updateQuery.push({
                label: key,
                labelValue: tag,
                queryValue: queryValue,
              })
            }
          })
        }
      })
    }
    setSelectedFilters(prev =>
      getUpdatedQueryAndLabels(prev, updateQuery, false),
    )
    setNoShow(id === 'noShow')

    if (id === 'noPresets') {
      setMappedFilters(prev => getUpdatedMappedFilters({ prev }))
    } else {
      const dateTime = new Date()
      let dateWeekNumber = moment(dateTime).isoWeek()
      const dateYearNumber = moment(dateTime).year()
      const weekQueryValue = {
        weekNumber: dateWeekNumber,
        year: dateYearNumber,
      }

      if (id === 'liveMissions') {
        const today = getLongWeekDay(getWeekDayIndex(dateTime))
        const timeOfDay = formatStringToFirstUpperCase(currentTimeOfDay)

        const updatePresetValues = [
          {
            label: 'Week',
            labelValue: `W${dateWeekNumber}`,
            queryValue: weekQueryValue,
          },
          {
            label: 'Time of day',
            labelValue: timeOfDay,
            queryValue: timeOfDay.toUpperCase(),
          },
          {
            label: 'Day of week',
            labelValue: today,
            queryValue: today.toUpperCase(),
          },
        ]

        updatePresetValues.forEach(({ label, labelValue }) => {
          setUrl(label, labelValue)
          setTagsAndSuggestions({ tag: labelValue, option: 'add' })
        })
        setSelectedFilters(prev =>
          getUpdatedQueryAndLabels(prev, updatePresetValues),
        )

        setMappedFilters(prev =>
          getUpdatedMappedFilters({
            prev,
            updatedValues: updatePresetValues,
            date: {
              dateWeekNumber,
              dateYearNumber,
            },
          }),
        )
      }
      if (id === 'yesterdayMissions') {
        const yesterday = getLongWeekDay(
          getWeekDayIndex(dateTime) == 0 ? 6 : getWeekDayIndex(dateTime) - 1,
        )

        if (yesterday === 'Sunday') {
          dateWeekNumber -= 1
        }

        const updatePresetValues = [
          {
            label: 'Week',
            labelValue: `W${dateWeekNumber}`,
            queryValue: weekQueryValue,
          },
          {
            label: 'Day of week',
            labelValue: yesterday,
            queryValue: yesterday.toUpperCase(),
          },
        ]

        updatePresetValues.forEach(({ label, labelValue }) => {
          setUrl(label, labelValue)
          setTagsAndSuggestions({ tag: labelValue, option: 'add' })
        })

        setSelectedFilters(prev =>
          getUpdatedQueryAndLabels(prev, updatePresetValues),
        )

        setMappedFilters(prev =>
          getUpdatedMappedFilters({
            prev,
            updatedValues: updatePresetValues,
            date: {
              dateWeekNumber,
              dateYearNumber,
            },
          }),
        )
      }
      if (id === 'todayMissions') {
        const today = getLongWeekDay(getWeekDayIndex(dateTime))

        const updatePresetValues = [
          {
            label: 'Week',
            labelValue: `W${dateWeekNumber}`,
            queryValue: weekQueryValue,
          },
          {
            label: 'Day of week',
            labelValue: today,
            queryValue: today.toUpperCase(),
          },
        ]

        updatePresetValues.forEach(({ label, labelValue }) => {
          setUrl(label, labelValue)
          setTagsAndSuggestions({ tag: labelValue, option: 'add' })
        })

        setSelectedFilters(prev =>
          getUpdatedQueryAndLabels(prev, updatePresetValues),
        )

        setMappedFilters(prev =>
          getUpdatedMappedFilters({
            prev,
            updatedValues: updatePresetValues,
            date: {
              dateWeekNumber,
              dateYearNumber,
            },
          }),
        )
      }
      if (id === 'tomorrowMissions') {
        const tomorrow = getLongWeekDay(getWeekDayIndex(dateTime) + 1)
        if (tomorrow === 'Monday') {
          dateWeekNumber += 1
        }
        const updatePresetValues = [
          {
            label: 'Week',
            labelValue: `W${dateWeekNumber}`,
            queryValue: weekQueryValue,
          },
          {
            label: 'Day of week',
            labelValue: tomorrow,
            queryValue: tomorrow.toUpperCase(),
          },
        ]

        updatePresetValues.forEach(({ label, labelValue }) => {
          setUrl(label, labelValue)
          setTagsAndSuggestions({ tag: labelValue, option: 'add' })
        })

        setSelectedFilters(prev =>
          getUpdatedQueryAndLabels(prev, updatePresetValues),
        )

        setMappedFilters(prev =>
          getUpdatedMappedFilters({
            prev,
            updatedValues: updatePresetValues,
            date: {
              dateWeekNumber,
              dateYearNumber,
            },
          }),
        )
      }
    }
  }

  const assignSpyToMissions = async spyId => {
    const response = await bookSpyMissions({
      userId: spyId,
      shiftMissions: selectedMissions.map(item => ({ missionId: item })),
      manuallyBooked: true,
    })

    const { data } = await response.json()

    const { success, shiftId, warningText } = data.bookSpyMissions

    if (success) {
      refetchAndDropSelectedMissions({
        countryId,
        cityId,
        first: resultPerPage,
        skip,
        orderBy,
        filter: searchText,
        ...selectedFilters.query,
      })
    } else {
      const text = (
        <span>
          Shifts on the same day, at the same time, already assigned to this
          Spy, please confirm your choice?
          {warningText ? (
            <span>
              <br />
              <br />

              <span style={{ color: '#004360' }}>{warningText}</span>
            </span>
          ) : null}
        </span>
      )
      setModalInformation({
        text,
        shiftId,
        isOpen: true,
        type: MODAL_TYPES.ADD_MISSION_ON_SHIFT,
        canBeSpied: null,
        missionId: null,
        userId: spyId,
      })
    }
  }
  const handleConfirmModal = async () => {
    if (modalInformation.type === MODAL_TYPES.DELETE_SPY) {
      await deleteSpyShift({
        id: modalInformation.shiftId,
      })

      setModalInformation(defaultModalState)

      refetchAndDropSelectedMissions({
        countryId,
        cityId,
        first: resultPerPage,
        skip,
        orderBy,
        filter: searchText,
        ...selectedFilters.query,
      })
    }

    if (modalInformation.type === MODAL_TYPES.UPDATE_MISSION) {
      const response = await updateMission({
        id: modalInformation.missionId,
        canBeSpied: modalInformation.canBeSpied,
      })
      const { data } = await response.json()
      const mission = data.updateMission.mission

      if (mission.id) {
        setChangedCheckBox(mission)
      }

      if (!modalInformation.canBeSpied && modalInformation.shiftId) {
        await deleteSpyShift({
          id: modalInformation.shiftId,
        })

        refetchAndDropSelectedMissions({
          countryId,
          cityId,
          first: resultPerPage,
          skip,
          orderBy,
          filter: searchText,
          ...selectedFilters.query,
        })
      }
      setModalInformation(defaultModalState)
    }

    if (modalInformation.type === MODAL_TYPES.ADD_MISSION_ON_SHIFT) {
      await addMissionOnExistingShift({
        userId: modalInformation.userId,
        shiftMissions: selectedMissions.map(item => ({ missionId: item })),
        manuallyBooked: true,
        shiftId: modalInformation.shiftId,
      })
      refetchAndDropSelectedMissions({
        countryId,
        cityId,
        first: resultPerPage,
        skip,
        orderBy,
        filter: searchText,
        ...selectedFilters.query,
      })

      setModalInformation(defaultModalState)
    }
  }

  const fetchMissionsByChunks = async (maxRows, cityIds) => {
    let i = 0
    let allMissions = []
    const fetchMissions = async (skip = 0) => {
      const chunkSize = 200
      let response
      const payload = {
        countryId: simpleCountryId,
        first: chunkSize,
        skip,
        orderBy,
        filter: searchText,
        ...selectedFilters.query,
      }
      if (cityIds.length > 0) {
        payload.cityIds = cityIds
      }
      response = await getMissionsByCountry(payload)

      const parsedResponse = await response.json()
      allMissions = [
        ...allMissions,
        ...parsedResponse.data.country.missions.edges,
      ]
      const hasNextPage =
        parsedResponse.data.country.missions.pageInfo.hasNextPage
      if (!hasNextPage || (hasNextPage && allMissions.length === maxRows)) {
        return
      }
      i++
      await fetchMissions(chunkSize * i)
    }
    await fetchMissions()
    return allMissions
  }

  const handleExportData = async (maxRows, cityNames, cityIds) => {
    try {
      const dataToExport = await fetchMissionsByChunks(maxRows, cityIds)
      exportCSV(dataToExport, cityNames, countryName, shouldHideSensitiveData)
      setIsExportLoading(false)
    } catch (error) {
      console.error(error)
      setIsExportLoading(false)
    }
  }

  const onExportData = () => {
    setIsExportLoading(true)
    const maxRows = 1000
    const totalCount = data.totalCount
    const cityIds = cityId
      ? [cityId]
      : Array.isArray(selectedFilters.query.cityIds)
      ? selectedFilters.query.cityIds
      : []

    const cityNames = cityId
      ? [cityName]
      : cityIds.reduce((acc, curr) => {
          const city = citiesList.find(city => city.id === curr)
          if (city) {
            acc.push(city.name)
          }
          return acc
        }, [])
    if (totalCount >= maxRows) {
      dispatch(
        showReduxModal({
          title: 'Warning',
          children: (
            <Box
              display="flex"
              flexWrap="wrap"
              justifyContent="center"
              mt={3}
              mb={3}
            >
              Data consists of {totalCount} rows, but the limit for export is{' '}
              {maxRows}. Do you want to proceed?
            </Box>
          ),
          onConfirm: () => {
            hideReduxModal()
            resetReduxModal()
            handleExportData(maxRows, cityNames, cityIds)
          },
          onCancel: () => {
            setIsExportLoading(false)
          },
        }),
      )
    } else {
      handleExportData(maxRows, cityNames, cityIds)
    }
  }

  const filterHeaders = (headers, isCountryLevel) => {
    let filteredHeaders = headers

    if (isSpy) {
      filteredHeaders = filteredHeaders.filter(header => {
        return !['flyerCode', 'metric1', 'metric2', 'rating'].includes(
          header.id,
        )
      })
    }

    if (isCityManagerAssistant) {
      filteredHeaders = filteredHeaders.filter(header => {
        return !['metric1', 'metric2'].includes(header.id)
      })
    }
    if (!isCountryLevel) {
      filteredHeaders = filteredHeaders.filter(
        header => !header.countryLevelHeader,
      )
    }
    return filteredHeaders
  }

  const filteredHeaders = filterHeaders(headers, countryId)

  const renderContent = () => (
    <Box>
      <ModalConfirmation
        key="modal-confirmation"
        modalShow={modalInformation.isOpen}
        handleCancel={() => setModalInformation(defaultModalState)}
        handleConfirm={handleConfirmModal}
        title="Confirmation needed"
        text={modalInformation.text}
        labelCancel="Cancel"
        labelConfirm="Confirm"
        closeButton={false}
      />
      <MasterScheduleWrapper>
        {countryId || isSpy ? null : (
          <Link
            to={match.url.replace('global-planning', 'edit')}
            className="link-edit-main"
          >
            <StyledIconButton>
              <EditIcon />
            </StyledIconButton>
          </Link>
        )}
        {mappedFilters.length || isLoaded ? (
          <Fragment>
            {!isSpy ? (
              <Button
                className={props.classes.exportButton}
                disabled={isExportLoading}
                circleSize={15}
                formSubmitting={isExportLoading}
                onClick={onExportData}
              >
                <span className={isExportLoading ? 'isLoading' : ''}>
                  Export
                </span>
              </Button>
            ) : null}
            <SearchAndFilters
              onChange={onChange}
              enableFilterSuggestions={true}
              filters={mappedFilters}
              searchText={searchText}
              className={classes.filtersContainer}
              suggestions={suggestions}
              activeTags={activeTags}
              setActiveTags={setActiveTags}
              setSuggestions={setSuggestions}
              onAddTag={onAddTag}
              onRemoveTag={onRemoveTag}
              onPressInput={onPressInput}
              onChangePreset={onChangePreset}
              presets={presets}
              presetValue={presetValue}
            />
            {isLoaded ? (
              <div className={classes.wrapperTable}>
                <EnhancedTable
                  headers={filteredHeaders}
                  rows={rowsData}
                  totalRow={totalRow}
                  handleRequestSort={handleRequestSort}
                  order={order}
                  orderBy={orderBy}
                  resultPerPage={resultPerPage}
                  onChangePage={onChangePage}
                  totalCount={totalCount}
                  onChangeRowsPerPage={onChangeRowsPerPage}
                  page={page}
                  overHeaderComponent={
                    <AssignSpyToolbar
                      numSelected={selectedMissions.length}
                      title={title}
                      spies={filteredSpies}
                      onConfirm={assignSpyToMissions}
                    />
                  }
                  onClickRow={onClickRow}
                  onContextMenuRow={onContextMenuRow}
                />
                <MissionContextMenu
                  anchor={contextMenu ? contextMenu.anchor : null}
                  row={contextMenu ? contextMenu.row : null}
                  onClose={() => setContextMenu(null)}
                  onDone={refetchAfterMenuAction}
                />
              </div>
            ) : (
              <div className={classes.loadingTableWrapper}>
                <LoadingIndicator />
              </div>
            )}
          </Fragment>
        ) : (
          <div className={classes.loadingAllWrapper}>
            <LoadingIndicator />
          </div>
        )}
      </MasterScheduleWrapper>
    </Box>
  )

  return countryId ? (
    renderContent()
  ) : (
    <CityPage
      active={props.nav}
      cityId={props.cityId}
      history={props.history}
      match={props.match}
      cityName={props.cityName}
      className={classes.wrapper}
      breadcrumbs={[{ label: 'Global Planning' }]}
    >
      {renderContent()}
    </CityPage>
  )
}

export default injectStyles(styles)(MasterSchedule)
